import { z } from 'zod';
import { taskTypeZod } from 'packages/lib/api/annotations/contract/enums';

/**
 * Every `groupType` the analytics group endpoints accept. All Pipelines and All
 * Requests are the two scopes that do not identify a single group.
 */
export const groupTypeZod = z.enum([
  'AllRequests',
  'AllPipelines',
  'RequestId',
  'Pipeline',
  'Workflow',
  'SampleGroup',
  'TaskGroup',
]);
export type GroupType = z.infer<typeof groupTypeZod>;

/** One group, with enough detail to name it and link to its analytics page. */
export const groupRefZod = z.object({
  groupType: groupTypeZod,
  token: z.string(),
  name: z.string(),
});
export type GroupRef = z.infer<typeof groupRefZod>;

/**
 * The chain from the top-level pipeline down to and including the requested group,
 * e.g. `[pipeline, workflow, sampleGroup]` when a sample group was requested.
 */
export const groupHierarchyResponseZod = z.object({
  data: z.array(groupRefZod),
});
export type GroupHierarchyResponse = z.infer<typeof groupHierarchyResponseZod>;

/**
 * `groupType` query of `GET /group/hierarchy`. `Task` resolves to the chain ending at
 * that task's task group; the response itself never contains a task.
 */
export type GroupHierarchyType = GroupType | 'Task';

/** Deduped TaskTypes from `GET /tasktypes`. */
export const taskTypesResponseZod = z.array(taskTypeZod);
export type TaskTypesResponse = z.infer<typeof taskTypesResponseZod>;

/**
 * All Pipelines and All Requests identify no single group, so they take no token.
 * Any level may be passed, including a task or contributor; only these two match.
 */
export const isTokenlessGroupType = (
  groupType: string
): groupType is 'AllPipelines' | 'AllRequests' =>
  groupType === 'AllPipelines' || groupType === 'AllRequests';

/**
 * Scope for `GET /cost/group`. The request sends this as the `groupType` query
 * param. `tokenOrId` is omitted on All Pipelines and All Requests; required everywhere else.
 */
export type CostGroupQuery =
  | {
      groupType: 'AllPipelines' | 'AllRequests';
      tokenOrId?: string;
    }
  | {
      groupType: Exclude<GroupType, 'AllPipelines' | 'AllRequests'>;
      /** A group token matching `groupType`, or the request ID when `groupType` is `RequestId`. */
      tokenOrId: string;
    };

type TokenlessScope = { groupType: 'AllPipelines' | 'AllRequests' };

/** Narrows the whole query, which comparing `groupType` alone does not. */
export const isTokenlessScope = <T extends CostGroupQuery>(
  scope: T
): scope is Extract<T, TokenlessScope> => isTokenlessGroupType(scope.groupType);

export const toCostGroupQuery = (
  groupType: GroupType,
  tokenOrId?: string
): CostGroupQuery | null => {
  if (isTokenlessGroupType(groupType)) return { groupType, tokenOrId };
  return tokenOrId ? { groupType, tokenOrId } : null;
};

/** Pages whose Task Types menu is populated by `GET /tasktypes`; a task has only one. */
export type TaskTypesScopeType = GroupType | 'Contributor';

/**
 * Query parameters for `GET /tasktypes`. `tokenOrId` is the request ID for `RequestId`,
 * the user's email for `Contributor`, and the group's token for every other type.
 */
export type TaskTypesQuery =
  | { type: 'AllPipelines' | 'AllRequests' }
  | {
      type: Exclude<TaskTypesScopeType, 'AllPipelines' | 'AllRequests'>;
      tokenOrId: string;
    };

/** Null until a type that needs a `tokenOrId` has one. */
export const toTaskTypesQuery = (
  type: TaskTypesScopeType,
  tokenOrId?: string
): TaskTypesQuery | null => {
  if (type === 'AllPipelines' || type === 'AllRequests') return { type };
  return tokenOrId ? { type, tokenOrId } : null;
};
